import styles from './top-tracks.module.css'

import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import React from 'react'

import { TRACK } from '@disco/constants/ItemTypes'
import { useTranslation } from '@disco/i18n/use-translation'

import type { ClientLibraryId } from 'types/client-library'
import type { Track, TrackId } from 'types/track'

import { Button, Flex, Spinner, Text } from 'components/ds'
import Sortable from 'components/ui/sortable'

import TopTrackRow from './top-track-row'

type Props = {
  libraryId: ClientLibraryId
  tracks: Track[]
  isLoaded: boolean
  hasError: boolean
  isSaving: boolean
  onRetry: () => void
  onAdd: () => void
  onRemove: (trackId: TrackId) => void
  onReorder: (dropResult: { order: string[] }) => void
}

/**
 * Body of the top tracks section: the sortable list, or its loading, error or empty state.
 * @param libraryId - ID of the artist profile catalog
 * @param tracks - Top tracks in order
 * @param isLoaded - Whether the top tracks have loaded
 * @param hasError - Whether loading the top tracks failed
 * @param isSaving - Whether a change is being saved
 * @param onRetry - Called to load the top tracks again after an error
 * @param onAdd - Called to open the add tracks dialog
 * @param onRemove - Called with a track's ID when it's removed
 * @param onReorder - Called with the new order when a track is dropped
 */
const TopTracksContent = ({
  libraryId,
  tracks,
  isLoaded,
  hasError,
  isSaving,
  onRetry,
  onAdd,
  onRemove,
  onReorder,
}: Props) => {
  const { t } = useTranslation(['catalogs-management', 'common'])

  if (!isLoaded && hasError) {
    return (
      <Flex direction="column" align="center" gap="3" p="4">
        <Text size="2" color="gray">
          {t(
            'catalogs-management:couldNotLoadTopTracks',
            'Couldn’t load top tracks.',
          )}
        </Text>
        <Button variant="soft" onClick={onRetry}>
          {t('common:tryAgain', 'Try again')}
        </Button>
      </Flex>
    )
  }
  // Also covers the first render, before the load has been dispatched
  if (!isLoaded) {
    return (
      <Flex justify="center" p="4">
        <Spinner />
      </Flex>
    )
  }
  if (tracks.length === 0) {
    return (
      <button
        type="button"
        className={styles.emptyState}
        disabled={isSaving}
        onClick={onAdd}
      >
        <Flex align="center" justify="center" gap="2">
          <LuPlus />
          <Text size="2">
            {t(
              'catalogs-management:addYourFirstTopTrack',
              'Add your first top track',
            )}
          </Text>
        </Flex>
      </button>
    )
  }

  return (
    <Sortable
      accept={[TRACK]}
      id={`${libraryId}-top-tracks`}
      items={tracks}
      keyExtractor={(track: Track) => String(track.id)}
      renderItem={(track: Track | undefined, index: number) =>
        // Sortable renders its previous order once before syncing to a removed track
        track && (
          <TopTrackRow
            track={track}
            index={index}
            disabled={isSaving}
            onRemove={onRemove}
          />
        )
      }
      onDrop={onReorder}
    />
  )
}

export default TopTracksContent
